import {
  Alert,
  Box,
  Button,
  Card,
  CopyButton,
  Group,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconCopy } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useInviteAdmin, type AssignableAdminRole, type InviteResult } from '../api/admin-users';

export function AdminUserInvitePage() {
  const navigate = useNavigate();
  const invite = useInviteAdmin();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<AssignableAdminRole>('support');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteResult | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Email format looks invalid.');
      return;
    }
    if (!displayName.trim()) {
      setError('Display name is required.');
      return;
    }

    invite.mutate(
      { email: email.trim(), displayName: displayName.trim(), role },
      {
        onSuccess: (r) => setResult(r),
        onError: (err) => setError(formatError(err)),
      },
    );
  };

  if (result) {
    return (
      <Stack gap="lg">
        <Title order={2}>Invitation created</Title>
        <Card shadow="sm" padding="lg" withBorder maw={720}>
          <Stack gap="md">
            <Text>
              Send <strong>{result.email}</strong> this link. It expires in 72 hours.
            </Text>
            <Text
              ff="monospace"
              size="sm"
              p="sm"
              style={{ wordBreak: 'break-all', background: 'var(--mantine-color-gray-0)' }}
            >
              {result.setupUrl}
            </Text>
            <Group>
              <CopyButton value={result.setupUrl} timeout={2000}>
                {({ copied, copy }) => (
                  <Button
                    onClick={copy}
                    leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    variant="default"
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                )}
              </CopyButton>
              <Button onClick={() => navigate('/admin-users')}>Done</Button>
            </Group>
            <Text size="sm" c="dimmed">
              This link is shown once. If lost, regenerate it from the user's edit page.
            </Text>
          </Stack>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Title order={2}>Invite admin</Title>
      <Card shadow="sm" padding="lg" maw={600}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack gap="md">
            {error && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                {error}
              </Alert>
            )}
            <TextInput
              label="Email"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={invite.isPending}
            />
            <TextInput
              label="Display name"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              disabled={invite.isPending}
            />
            <NativeSelect
              label="Role"
              required
              data={[
                { value: 'support', label: 'support' },
                { value: 'admin', label: 'admin' },
              ]}
              value={role}
              onChange={(e) => setRole(e.currentTarget.value as AssignableAdminRole)}
              disabled={invite.isPending}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" component={Link} to="/admin-users">
                Cancel
              </Button>
              <Button type="submit" loading={invite.isPending}>
                Invite
              </Button>
            </Group>
          </Stack>
        </Box>
      </Card>
    </Stack>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return 'An admin with this email already exists.';
    if (err.status === 400) return 'Validation failed. Check the highlighted fields.';
    if (err.status === 401) return 'Your session has expired. Please sign in again.';
    if (err.status === 403) return 'Your account does not have permission for this action.';
  }
  return 'Failed to invite admin.';
}
